'use client';

import { type FormEvent, useEffect, useState } from 'react';
import {
  ActionIcon,
  Button,
  Input,
  Modal,
  Text,
  Textarea,
  Title,
} from 'rizzui';
import { PiCheckCircleBold, PiGavelBold, PiXBold } from 'react-icons/pi';
import type { PublicShipment } from '@/server/api';

const BID_CONTACT_CACHE_KEY = 'shipsmart:available-loads-bid-contact:v1';

type BidContact = {
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
};

type BidForm = BidContact & {
  amount: string;
  message: string;
};

type BidFieldErrors = Partial<Record<keyof BidForm, string>>;

const EMPTY_CONTACT: BidContact = {
  fullName: '',
  email: '',
  phone: '',
  companyName: '',
};

function readCachedContact(): BidContact {
  if (typeof window === 'undefined') return EMPTY_CONTACT;

  try {
    const cached = JSON.parse(
      window.localStorage.getItem(BID_CONTACT_CACHE_KEY) ?? '{}'
    ) as Partial<BidContact>;

    return {
      fullName: typeof cached.fullName === 'string' ? cached.fullName : '',
      email: typeof cached.email === 'string' ? cached.email : '',
      phone: typeof cached.phone === 'string' ? cached.phone : '',
      companyName:
        typeof cached.companyName === 'string' ? cached.companyName : '',
    };
  } catch {
    return EMPTY_CONTACT;
  }
}

function cacheContact(contact: BidContact) {
  window.localStorage.setItem(BID_CONTACT_CACHE_KEY, JSON.stringify(contact));
}

function formatLocation(location: PublicShipment['pickup']) {
  return [location.city, location.state].filter(Boolean).join(', ') || ', ';
}

function formatPrice(value: number | null) {
  return value === null
    ? 'Call for rate'
    : new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(value);
}

function validateBidForm(form: BidForm): BidFieldErrors {
  const errors: BidFieldErrors = {};
  const amount = Number(form.amount.replace(/[$,\s]/g, ''));
  const phoneDigits = form.phone.replace(/\D/g, '');

  if (!Number.isFinite(amount) || amount <= 0) {
    errors.amount = 'Enter a bid greater than $0.';
  }
  if (form.fullName.trim().split(/\s+/).length < 2) {
    errors.fullName = 'Enter your first and last name.';
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  if (phoneDigits.length < 7) {
    errors.phone = 'Enter a valid phone number.';
  }
  if (!form.companyName.trim()) {
    errors.companyName = 'Enter your company name.';
  }

  return errors;
}

function normalizePhone(value: string) {
  const digits = value.replace(/\D/g, '');
  if (value.trim().startsWith('+')) return `+${digits}`;
  return digits.length === 10 ? `+1${digits}` : `+${digits}`;
}

function responseError(payload: unknown, fallback: string) {
  if (!payload || typeof payload !== 'object') return fallback;
  const response = payload as {
    message?: unknown;
    errors?: Record<string, unknown>;
  };
  const fieldError = response.errors
    ? Object.values(response.errors)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .find((value): value is string => typeof value === 'string')
    : undefined;
  return (
    fieldError ||
    (typeof response.message === 'string' ? response.message : fallback)
  );
}

type AvailableLoadBidModalProps = {
  load: PublicShipment | null;
  onClose: () => void;
  onBidSubmitted: () => Promise<void>;
};

export function AvailableLoadBidModal({
  load,
  onClose,
  onBidSubmitted,
}: AvailableLoadBidModalProps) {
  const [form, setForm] = useState<BidForm>({
    amount: '',
    message: '',
    ...EMPTY_CONTACT,
  });
  const [fieldErrors, setFieldErrors] = useState<BidFieldErrors>({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (!load) return;

    setForm({ amount: '', message: '', ...readCachedContact() });
    setFieldErrors({});
    setSubmitError('');
    setIsSubmitting(false);
    setIsSubmitted(false);
  }, [load]);

  const updateField = <Key extends keyof BidForm>(
    key: Key,
    value: BidForm[Key]
  ) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFieldErrors((current) => ({ ...current, [key]: undefined }));
    setSubmitError('');
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!load) return;

    const errors = validateBidForm(form);

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const [firstName, ...lastNameParts] = form.fullName.trim().split(/\s+/);
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(`/api/public/shipments/${load.pro}/bids`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastNameParts.join(' '),
          email: form.email.trim(),
          amount: Number(form.amount.replace(/[$,\s]/g, '')),
          currency: 'USD',
          company_name: form.companyName.trim(),
          phone: normalizePhone(form.phone),
          message: form.message.trim(),
        }),
      });
      const payload = (await response.json().catch(() => null)) as {
        success?: boolean;
      } | null;

      if (!response.ok || payload?.success === false) {
        throw new Error(
          responseError(payload, 'The bid could not be submitted.')
        );
      }

      cacheContact({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        companyName: form.companyName.trim(),
      });
      setIsSubmitted(true);
      onClose();
      void onBidSubmitted();
    } catch (error) {
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'The bid could not be submitted.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={load !== null}
      onClose={onClose}
      customSize="920px"
      rounded="xl"
      overlayClassName="bg-[#06162b]/55 backdrop-blur-[3px]"
      containerClassName="available-load-bid-modal !w-[calc(100vw-2rem)] overflow-hidden border border-white/10 bg-white p-0 shadow-[0_32px_90px_rgba(4,20,40,0.32)] sm:!w-[calc(100vw-3rem)] lg:!w-[920px]"
      className="z-[10020]"
    >
      <header className="available-load-bid-modal__header flex items-start justify-between gap-4 border-b border-[#e5ebf2] bg-[#f7fbff] px-5 py-5 sm:px-6">
        <div className="flex min-w-0 items-start gap-3.5">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#081e37] text-[#ffd166] shadow-[0_8px_22px_rgba(8,30,55,0.18)]">
            <PiGavelBold className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <Title
              as="h2"
              className="text-xl font-semibold tracking-[-0.035em] text-[#081e37]"
            >
              Make an offer
            </Title>
            <Text className="mt-1 text-sm leading-5 text-[#68778a]">
              Enter the rate you can move this load for.
            </Text>
          </div>
        </div>
        <ActionIcon
          type="button"
          variant="text"
          rounded="full"
          aria-label="Close bid form"
          onClick={onClose}
          className="available-load-bid-modal__close shrink-0 text-[#526174] hover:bg-[#eaf2f9]"
        >
          <PiXBold className="h-5 w-5" aria-hidden="true" />
        </ActionIcon>
      </header>

      {isSubmitted ? (
        <div className="available-load-bid-modal__success px-5 py-8 text-center sm:px-8 sm:py-10">
          <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#e9f8f0] text-[#148354]">
            <PiCheckCircleBold className="h-8 w-8" aria-hidden="true" />
          </span>
          <Title
            as="h3"
            className="mt-5 text-2xl font-semibold tracking-[-0.04em] text-[#081e37]"
          >
            Bid submitted
          </Title>
          <Text className="mx-auto mt-2 max-w-md text-sm leading-6 text-[#68778a]">
            Your offer for load #{load?.pro} was sent successfully. Dispatch can
            now review your rate and contact details.
          </Text>
          <div className="mt-7 flex justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsSubmitted(false)}
              className="available-load-bid-modal__secondary-action border-[#ccd7e3] text-[#081e37]"
            >
              Edit details
            </Button>
            <Button
              type="button"
              onClick={onClose}
              className="bg-[#081e37] text-white hover:bg-[#123556]"
            >
              Close
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div className="max-h-[min(70dvh,680px)] overflow-y-auto px-5 py-5 sm:px-6 sm:py-6 lg:max-h-none lg:overflow-visible">
            {load ? (
              <div className="available-load-bid-modal__load-summary mb-5 grid grid-cols-[1fr_auto] gap-4 rounded-2xl border border-[#dbe6f0] bg-[#f7fbff] p-4">
                <div className="min-w-0">
                  <Text className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8190a2]">
                    Load #{load.pro}
                  </Text>
                  <Text className="mt-1 truncate text-sm font-semibold text-[#081e37] sm:text-base">
                    {formatLocation(load.pickup)} →{' '}
                    {formatLocation(load.destination)}
                  </Text>
                </div>
                <div className="text-right">
                  <Text className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#8190a2]">
                    Listed rate
                  </Text>
                  <Text className="mt-1 text-base font-bold text-[#087ac0]">
                    {formatPrice(load.price)}
                  </Text>
                </div>
              </div>
            ) : null}

            <div className="space-y-4">
              <Input
                label="Your bid"
                value={form.amount}
                onChange={(event) => updateField('amount', event.target.value)}
                placeholder="0"
                inputMode="decimal"
                prefix={<span className="font-semibold text-[#526174]">$</span>}
                inputClassName="h-12 rounded-xl border-[#ccd7e3] text-base font-semibold text-[#081e37]"
                helperText="Enter an amount greater than $0."
                error={fieldErrors.amount}
              />

              <div className="available-load-bid-modal__contact border-t border-[#e5ebf2] pt-4">
                <Text className="mb-3 text-xs font-bold uppercase tracking-[0.09em] text-[#8190a2]">
                  Contact details
                </Text>
                <div className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input
                      label="Full name"
                      value={form.fullName}
                      onChange={(event) =>
                        updateField('fullName', event.target.value)
                      }
                      placeholder="First and last name"
                      autoComplete="name"
                      inputClassName="h-12 rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
                      error={fieldErrors.fullName}
                    />
                    <Input
                      label="Company name"
                      value={form.companyName}
                      onChange={(event) =>
                        updateField('companyName', event.target.value)
                      }
                      placeholder="Transport company"
                      autoComplete="organization"
                      inputClassName="h-12 rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
                      error={fieldErrors.companyName}
                    />
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Input
                      type="email"
                      label="Email"
                      value={form.email}
                      onChange={(event) =>
                        updateField('email', event.target.value)
                      }
                      placeholder="name@company.com"
                      autoComplete="email"
                      inputClassName="h-12 rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
                      error={fieldErrors.email}
                    />
                    <Input
                      type="tel"
                      label="Phone number"
                      value={form.phone}
                      onChange={(event) =>
                        updateField('phone', event.target.value)
                      }
                      placeholder="(555) 000-0000"
                      autoComplete="tel"
                      inputClassName="h-12 rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
                      error={fieldErrors.phone}
                    />
                  </div>
                  <Textarea
                    label="Message"
                    value={form.message}
                    onChange={(event) =>
                      updateField('message', event.target.value)
                    }
                    placeholder="Truck availability or other details"
                    textareaClassName="min-h-[92px] rounded-xl border-[#ccd7e3] text-base text-[#081e37]"
                  />
                </div>
              </div>

              {submitError ? (
                <Text className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm leading-5 text-red-700">
                  {submitError}
                </Text>
              ) : null}
            </div>
          </div>

          <footer className="available-load-bid-modal__footer flex items-center justify-between gap-3 border-t border-[#e5ebf2] bg-white px-5 py-4 sm:px-6">
            <Text className="hidden max-w-[250px] text-xs leading-5 text-[#8190a2] sm:block">
              Contact details will be remembered on this device.
            </Text>
            <div className="ml-auto flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="available-load-bid-modal__secondary-action border-[#ccd7e3] text-[#081e37]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                isLoading={isSubmitting}
                disabled={isSubmitting}
                className="available-load-bid-modal__submit bg-[#087ac0] text-white hover:bg-[#066aa8]"
              >
                Submit bid
              </Button>
            </div>
          </footer>
        </form>
      )}
    </Modal>
  );
}
